import React, { useEffect, useMemo, useState } from 'react'
import { Crosshair, Search, X } from 'lucide-react'
import {
  buildInsertFunctionArgumentDrafts,
  buildInsertFunctionArgumentSlots,
  buildInsertFunctionFilteredSignatures,
  buildInsertFunctionFormulaPreview,
  buildInsertFunctionInitialKey,
  describeInsertFunctionCategory,
  formatInsertFunctionSignature,
  INSERT_FUNCTION_CATEGORY_OPTIONS,
  normalizeInsertFunctionArgsForCommit,
  resolveInsertFunctionCatalogName,
  selectInsertFunctionSignature,
  type InsertFunctionCategory,
} from '../../spreadsheet/insertFunctionPresentation'
import './ExcelWorkbookChrome.css'

type ExcelInsertFunctionDialogProps = {
  onClose: () => void
  onInsert: (name: string, args: string[]) => void
  onEvaluateDraft?: (formula: string) => Promise<string>
  onPickRange?: () => string | null | Promise<string | null>
  initialFunctionName?: string | null
  initialArgs?: string[]
}

const EMPTY_INITIAL_ARGS: string[] = []

export function ExcelInsertFunctionDialog({
  onClose,
  onInsert,
  onEvaluateDraft,
  onPickRange,
  initialFunctionName,
  initialArgs = EMPTY_INITIAL_ARGS,
}: ExcelInsertFunctionDialogProps) {
  const [searchText, setSearchText] = useState('')
  const [category, setCategory] = useState<InsertFunctionCategory>('All')
  const filtered = useMemo(() => (
    buildInsertFunctionFilteredSignatures({ category, searchText })
  ), [category, searchText])
  const initialCatalogName = useMemo(() => (
    resolveInsertFunctionCatalogName(initialFunctionName)
  ), [initialFunctionName])
  const [selectedName, setSelectedName] = useState(() => initialCatalogName ?? filtered[0]?.name ?? '')
  const selected = useMemo(
    () => selectInsertFunctionSignature({ selectedName, filtered }),
    [filtered, selectedName],
  )
  const slots = useMemo(() => buildInsertFunctionArgumentSlots(selected), [selected])
  const [argDrafts, setArgDrafts] = useState<string[]>(() => slots.map(() => ''))
  const [preview, setPreview] = useState('')
  const [appliedInitialKey, setAppliedInitialKey] = useState('')

  useEffect(() => {
    if (!filtered.some((signature) => signature.name === selectedName)) {
      setSelectedName(filtered[0]?.name ?? '')
    }
  }, [filtered, selectedName])

  useEffect(() => {
    setArgDrafts((current) => buildInsertFunctionArgumentDrafts({ slots, currentDrafts: current }))
  }, [slots])

  useEffect(() => {
    if (!initialCatalogName) return
    const key = buildInsertFunctionInitialKey({ initialCatalogName, initialArgs })
    if (appliedInitialKey === key) return
    setSelectedName(initialCatalogName)
    const initialSignature = selectInsertFunctionSignature({
      selectedName: initialCatalogName,
      filtered,
    })
    setArgDrafts(buildInsertFunctionArgumentDrafts({
      slots: buildInsertFunctionArgumentSlots(initialSignature),
      initialArgs,
    }))
    setAppliedInitialKey(key)
  }, [appliedInitialKey, filtered, initialArgs, initialCatalogName])

  const formulaPreview = buildInsertFunctionFormulaPreview(selected.name, argDrafts)

  useEffect(() => {
    let cancelled = false
    if (!onEvaluateDraft) {
      setPreview('')
      return
    }
    const timer = window.setTimeout(() => {
      void onEvaluateDraft(formulaPreview).then((value) => {
        if (!cancelled) setPreview(value)
      }).catch((error) => {
        if (!cancelled) setPreview(error instanceof Error ? error.message : String(error))
      })
    }, 180)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [formulaPreview, onEvaluateDraft])

  return (
    <div className="fg-excel-format-dialog-backdrop" onMouseDown={onClose}>
      <section
        className="fg-excel-insert-function-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Insert Function"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="fg-excel-format-dialog-header">
          <strong>Insert Function</strong>
          <button type="button" className="fg-excel-icon-button" aria-label="Close Insert Function" title="Close Insert Function" onClick={onClose}>
            <X size={16} />
          </button>
        </header>
        <div className="fg-excel-insert-function-body">
          <div className="fg-excel-insert-function-browser">
            <label className="fg-excel-insert-function-search">
              <Search size={15} />
              <input
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                aria-label="Search functions"
                autoFocus
                data-lpignore="true"
                autoComplete="off"
              />
            </label>
            <select value={category} onChange={(event) => setCategory(event.target.value as InsertFunctionCategory)} aria-label="Function category">
              {INSERT_FUNCTION_CATEGORY_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
            <div className="fg-excel-insert-function-list" role="listbox" aria-label="Functions">
              {filtered.map((signature) => (
                <button
                  key={signature.name}
                  type="button"
                  role="option"
                  aria-selected={signature.name === selected.name}
                  data-selected={signature.name === selected.name ? 'true' : 'false'}
                  onClick={() => setSelectedName(signature.name)}
                >
                  <span>{signature.name}</span>
                  <small>{describeInsertFunctionCategory(signature)}</small>
                </button>
              ))}
            </div>
          </div>
          <div className="fg-excel-function-arguments">
            <div className="fg-excel-function-signature">{formatInsertFunctionSignature(selected)}</div>
            <p>{selected.description}</p>
            {selected.details?.map((detail) => <small key={detail}>{detail}</small>)}
            <div className="fg-excel-function-argument-grid">
              {slots.length === 0 ? (
                <div className="fg-excel-function-no-args">No arguments</div>
              ) : slots.map((slot, index) => (
                <label key={`${selected.name}:${slot}:${index}`}>
                  <span>{slot}</span>
                  <div className="fg-excel-function-argument-input">
                    <input
                      value={argDrafts[index] ?? ''}
                      onChange={(event) => {
                        const next = [...argDrafts]
                        next[index] = event.target.value
                        setArgDrafts(next)
                      }}
                      data-lpignore="true"
                      autoComplete="off"
                    />
                    {onPickRange && (
                      <button
                        type="button"
                        className="fg-excel-icon-button"
                        title={`Use current selection for ${slot}`}
                        aria-label={`Use current selection for ${slot}`}
                        onClick={async () => {
                          const picked = await Promise.resolve(onPickRange())
                          if (!picked) return
                          const next = [...argDrafts]
                          next[index] = picked
                          setArgDrafts(next)
                        }}
                      >
                        <Crosshair size={14} />
                      </button>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="fg-excel-function-preview" title={formulaPreview}>
              <span>{formulaPreview}</span>
              {preview && <strong>{preview}</strong>}
            </div>
          </div>
        </div>
        <footer className="fg-excel-cell-metadata-footer">
          <button type="button" className="fg-excel-cell-metadata-button" onClick={onClose}>Cancel</button>
          <button type="button" className="fg-excel-cell-metadata-button fg-excel-cell-metadata-button-primary" onClick={() => onInsert(selected.name, normalizeInsertFunctionArgsForCommit(argDrafts))}>
            Insert
          </button>
        </footer>
      </section>
    </div>
  )
}
